import EventIcon from "@mui/icons-material/Event";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import PeopleIcon from "@mui/icons-material/People";
import PinDropIcon from "@mui/icons-material/PinDrop";
import TodayIcon from "@mui/icons-material/Today";
import { Alert, Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, GridLegacy as Grid, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getEventBySlug, registerForEvent } from "../api/event";
import { AuthContext } from "../contexts/AuthContext";
import { SiteStateContext } from "../contexts/SiteStateContext";
import { fixSiteState } from "../util/site_state";
import RichRender from "../components/RichRender";
import gaEvents from "../util/gaEvent";
import { LoadingButton } from "@mui/lab";

const dateFormat: string = "MMM DD, YYYY h:mma";

// Block type constants matching the enum
const BlockTypes = {
    TEXT: 0,
    NUMBER: 1,
    DATE: 2,
    TIME: 3,
    CHECKBOX: 4,
    DROPDOWN: 5,
    RADIO: 6,
    FILE: 7,
    IMAGE: 8,
    VIDEO: 9,
    LINK: 10,
    EMAIL: 11,
    PHONE: 12,
    ADDRESS: 13,
    NAME: 14,
    GROUP: 15,
    NOTES: 16,
    CUSTOM: 17,
    UNIT: 18,
};

// Age group options - keep in sync with api/routes/EventRoutes.ts
const AGE_GROUP_OPTIONS = [
    { value: "youth", label: "Youth (<18)" },
    { value: "ya", label: "Youth+ (18-21)" },
    { value: "adult", label: "Adult (>21)" },
];
const AGE_GROUP_DEFAULT = "youth";

interface BlockRendererProps {
    block: EventObject;
    value: string;
    setValue: (val: string) => void;
    unitType?: string;
    setUnitType?: (val: string) => void;
}

function BlockRenderer({ block, value, setValue, unitType, setUnitType }: BlockRendererProps) {
    if (!block.enabled) return null;

    switch (block.type) {
        case BlockTypes.TEXT:
            return (
                <TextField
                    label={block.title}
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.NUMBER:
            return (
                <TextField
                    label={block.title}
                    type="number"
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.DROPDOWN:
            return (
                <TextField
                    select
                    label={block.title}
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                >
                    {block.options?.split(",").map((option) => (
                        <MenuItem key={option.trim()} value={option.trim()}>
                            {option.trim()}
                        </MenuItem>
                    ))}
                </TextField>
            );
        case BlockTypes.CHECKBOX:
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === "true"}
                            onChange={(e) => setValue(e.target.checked ? "true" : "false")}
                        />
                    }
                    label={block.title}
                />
            );
        case BlockTypes.EMAIL:
            return (
                <TextField
                    label={block.title}
                    type="email"
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.PHONE:
            return (
                <TextField
                    label={block.title}
                    type="tel"
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.NAME:
            return (
                <TextField
                    label={block.title}
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.NOTES:
            return (
                <TextField
                    label={block.title}
                    variant="standard"
                    fullWidth
                    multiline
                    rows={3}
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
        case BlockTypes.GROUP:
            return (
                <TextField
                    select
                    label={block.title}
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                >
                    {AGE_GROUP_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>
            );
        case BlockTypes.UNIT:
            return (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            select
                            label="Unit Type"
                            variant="standard"
                            fullWidth
                            required={block.required}
                            value={unitType || "troop"}
                            onChange={(e) => setUnitType && setUnitType(e.target.value)}
                        >
                            <MenuItem value="troop">Troop</MenuItem>
                            <MenuItem value="crew">Crew</MenuItem>
                            <MenuItem value="ship">Ship</MenuItem>
                            <MenuItem value="pack">Pack</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Unit Number"
                            type="number"
                            variant="standard"
                            fullWidth
                            required={block.required}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            helperText={block.helper_text || undefined}
                        />
                    </Grid>
                </Grid>
            );
        default:
            return (
                <TextField
                    label={block.title}
                    variant="standard"
                    fullWidth
                    required={block.required}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    helperText={block.helper_text || undefined}
                />
            );
    }
}

const EventPage = () => {
    const { setSiteState } = useContext(SiteStateContext);
    const [event, setEvent] = useState<PublicEventItem | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [registerInProgress, setRegisterInProgress] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // Block values stored by block ID
    const [blockValues, setBlockValues] = useState<Record<string, string>>({});
    const [unitType, setUnitType] = useState<string>("troop");

    const [successState, setSuccessState] = useState<"success" | "fail" | "unset">("unset");
    const [errorState, setErrorState] = useState<string>("");
    const [attemptingRegister, setAttemptingRegister] = useState<boolean>(false);

    const [eventClosed, setEventClosed] = useState<boolean>(false);
    const [adminEventOverride, setAdminEventOverride] = useState<boolean>(false);
    const { authUser } = useContext(AuthContext);

    const [disableActions, setDisableActions] = useState<boolean>(false);

    useEffect(() => {
        if (setSiteState) {
            setSiteState(
                fixSiteState({
                    title: `${event?.title ?? "Event Not Found"} - Events`,
                    navHighlight: "/events",
                    actions: [
                        {
                            icon: ModeEditIcon,
                            text: "Edit Event",
                            disabled: disableActions,
                            onClick: () => {
                                navigate(`/admin/events/${event?.id}/edit`);
                            },
                        },
                        {
                            icon: PeopleIcon,
                            text: "View Registrants",
                            disabled: disableActions,
                            onClick: () => {
                                navigate(`/admin/events/${event?.id}/view`);
                            },
                        },
                    ],
                })
            );
        }
    }, [setSiteState, event, disableActions, navigate]);

    useEffect(() => {
        if (!params.slug) return setLoading(false);
        getEventBySlug(params.slug)
            .then((ev) => {
                if (ev.external) {
                    window.location.href = ev.link;
                    return;
                }
                if (authUser) setAdminEventOverride(true);
                setEventClosed(!ev.enabled);
                setEvent(ev);

                // Initialize block values with defaults
                const initialValues: Record<string, string> = {};
                if (ev.objects) {
                    const sortedObjects = [...ev.objects].sort((a, b) => a.order - b.order);
                    sortedObjects.forEach((obj) => {
                        if (obj.type === BlockTypes.GROUP) {
                            initialValues[obj.id] = obj.default || AGE_GROUP_DEFAULT;
                        } else {
                            initialValues[obj.id] = obj.default || "";
                        }
                    });
                }
                setBlockValues(initialValues);
                setLoading(false);
                gaEvents.events.view(ev.id);
            })
            .catch((err) => {
                console.warn("Failed to load event:", err);
                setDisableActions(true);
                setLoading(false);
            });
    }, [location, params, authUser]);

    function resetInputs() {
        const initialValues: Record<string, string> = {};
        if (event?.objects) {
            event.objects.forEach((obj) => {
                if (obj.type === BlockTypes.GROUP) {
                    initialValues[obj.id] = obj.default || AGE_GROUP_DEFAULT;
                } else {
                    initialValues[obj.id] = obj.default || "";
                }
            });
        }
        setBlockValues(initialValues);
        setUnitType("troop");
    }

    function buildRegistrationData() {
        const data: EventRegistrationPost = {
            name: "",
            email: "",
            phone: "",
            unit: "",
            group: "",
            notes: "",
            objects: {},
        };

        if (!event?.objects) return data;

        const sortedObjects = [...event.objects].sort((a, b) => a.order - b.order);
        sortedObjects.forEach((obj) => {
            if (!obj.enabled) return;
            const value = blockValues[obj.id] || "";
            switch (obj.type) {
                case BlockTypes.NAME:
                    data.name = value;
                    break;
                case BlockTypes.EMAIL:
                    data.email = value;
                    break;
                case BlockTypes.PHONE:
                    data.phone = value;
                    break;
                case BlockTypes.UNIT:
                    data.unit = unitType + value;
                    break;
                case BlockTypes.GROUP:
                    data.group = value;
                    break;
                case BlockTypes.NOTES:
                    data.notes = value;
                    break;
                default:
                    // Custom blocks go into objects
                    data.objects[obj.id] = value;
                    break;
            }
        });

        return data;
    }

    const sortedBlocks = event?.objects ? [...event.objects].sort((a, b) => a.order - b.order) : [];

    return (
        <Container>
            {loading ? (
                <CircularProgress />
            ) : event ? (
                <Paper sx={{ m: 4, p: 3 }}>
                    {eventClosed && adminEventOverride && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This event is disabled — because you are an admin, you still see this event.
                        </Alert>
                    )}
                    {successState === "fail" && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorState ?? "An unknown error occurred."}
                        </Alert>
                    )}
                    {successState === "success" && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            You have successfully registered for this event!
                        </Alert>
                    )}
                    <Stack direction="row">
                        <Typography variant="h3">{event.title}</Typography>
                        {registerInProgress && (
                            <Button
                                size="small"
                                sx={{ marginLeft: "auto" }}
                                variant="outlined"
                                onClick={() => {
                                    setSuccessState("unset");
                                    setRegisterInProgress(false);
                                }}
                            >
                                Return to event
                            </Button>
                        )}
                    </Stack>
                    {eventClosed && !adminEventOverride ? (
                        <>
                            <Grid container>
                                <Grid item xs={12} md={9} sx={{ p: 3 }}>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Registration is closed for this event.
                                    </Alert>
                                    <RichRender html={event.description} />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Stack spacing={1}>
                                        <Stack direction={"row"} spacing={1}>
                                            <TodayIcon />
                                            <Typography variant="body2">{dayjs(event.start).format(dateFormat)}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} spacing={1}>
                                            <EventIcon />
                                            <Typography variant="body2">{dayjs(event.end).format(dateFormat)}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} spacing={1}>
                                            <PinDropIcon />
                                            <Typography variant="body2">{event.location}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} spacing={1}>
                                            <PeopleIcon />
                                            <Typography variant="body2">{event.registration_count} registrants</Typography>
                                        </Stack>
                                    </Stack>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        sx={{ mt: 6 }}
                                        onClick={() => {
                                            navigate("/events");
                                        }}
                                    >
                                        View all events
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <>
                            {registerInProgress ? (
                                <Box sx={{ p: 3 }}>
                                    <Grid container spacing={3}>
                                        {sortedBlocks.filter(b => b.enabled).map((block) => (
                                            <Grid item xs={12} key={block.id}>
                                                <BlockRenderer
                                                    block={block}
                                                    value={blockValues[block.id] || ""}
                                                    setValue={(val) => setBlockValues({ ...blockValues, [block.id]: val })}
                                                    unitType={unitType}
                                                    setUnitType={setUnitType}
                                                />
                                            </Grid>
                                        ))}
                                        <Grid item xs={12}>
                                            <LoadingButton
                                                variant="contained"
                                                fullWidth
                                                loading={attemptingRegister}
                                                onClick={() => {
                                                    setAttemptingRegister(true);
                                                    const regData = buildRegistrationData();
                                                    registerForEvent(event.id, regData)
                                                        .then((success) => {
                                                            setAttemptingRegister(false);
                                                            if (success) {
                                                                gaEvents.events.register(event.id);
                                                                setSuccessState("success");
                                                                setRegisterInProgress(false);
                                                                resetInputs();
                                                                setEvent({ ...event, registration_count: event.registration_count + 1 });
                                                            } else {
                                                                setSuccessState("fail");
                                                                setRegisterInProgress(false);
                                                                resetInputs();
                                                                console.warn("Failed to register for event");
                                                            }
                                                        })
                                                        .catch((err) => {
                                                            setAttemptingRegister(false);
                                                            setErrorState(err);
                                                            setSuccessState("fail");
                                                            setRegisterInProgress(false);
                                                            resetInputs();
                                                            console.warn("Failed to register for event");
                                                        });
                                                }}
                                            >
                                                Register
                                            </LoadingButton>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Grid container>
                                    <Grid item xs={12} md={9} sx={{ p: 3 }}>
                                        <RichRender html={event.description} />
                                    </Grid>
                                    <Grid item xs={12} md={3} spacing={2}>
                                        <Stack spacing={1}>
                                            <Stack direction={"row"} spacing={1}>
                                                <TodayIcon />
                                                <Typography variant="body2">{dayjs(event.start).format(dateFormat)}</Typography>
                                            </Stack>
                                            <Stack direction={"row"} spacing={1}>
                                                <EventIcon />
                                                <Typography variant="body2">{dayjs(event.end).format(dateFormat)}</Typography>
                                            </Stack>
                                            <Stack direction={"row"} spacing={1}>
                                                <PinDropIcon />
                                                <Typography variant="body2">{event.location}</Typography>
                                            </Stack>
                                            <Stack direction={"row"} spacing={1}>
                                                <PeopleIcon />
                                                <Typography variant="body2">{event.registration_count} registrants</Typography>
                                            </Stack>
                                        </Stack>
                                        <Stack spacing={2}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                sx={{ mt: 6 }}
                                                onClick={() => {
                                                    setRegisterInProgress(true);
                                                    setSuccessState("unset");
                                                }}
                                            >
                                                Register
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    navigate("/events");
                                                }}
                                            >
                                                View all events
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                </Paper>
            ) : (
                <div>Event Not Found</div>
            )}
        </Container>
    );
};

export default EventPage;
